import {
    Box,
    Card,
    CardContent,
    Stack,
    styled,
    Typography,
} from '@mui/material';
import { Chart } from './Chart';
import { FunctionComponent, useState } from 'react';
import { ApexOptions } from 'apexcharts';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

type PieDiagrammProps = {
    chartSeries: ApexOptions['series'];
    labels: ApexOptions['labels'];
    title: string;
    subtitle?: string;
    counts?: number[];
};

export const PieDiagram: FunctionComponent<PieDiagrammProps> = ({
    labels,
    chartSeries,
    title,
    subtitle,
    counts,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const totalCount = counts?.reduce((sum, c) => sum + c, 0) ?? 0;

    const colors = [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.secondary.main,

        theme.palette.error.dark,
        theme.palette.info.dark,
        theme.palette.primary.dark,
    ];

    return (
        <Card
            sx={{
                height: '100%',
            }}
        >
            <CardContent
                sx={{
                    pb: 2,
                }}
            >
                <Typography variant='h6'>{title}</Typography>
                <Typography
                    sx={{
                        ml: 1,
                    }}
                    variant='subtitle1'
                >
                    {subtitle || `(${t('relativeToTotalRevenue')})`}
                </Typography>
            </CardContent>
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',

                    ['& .apexcharts-legend-text']: {
                        display: 'inline-block',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        width: '180px !important',
                    },
                }}
            >
                <Box
                    sx={{
                        mx: 'auto',
                    }}
                >
                    {chartSeries.length ? (
                        <Chart
                            series={chartSeries}
                            options={{
                                chart: {
                                    type: 'donut',
                                    events: {
                                        dataPointMouseEnter: (
                                            _event,
                                            _chartContext,
                                            opts
                                        ) => {
                                            setHoveredIndex(
                                                opts.dataPointIndex
                                            );
                                        },
                                        dataPointMouseLeave: () => {
                                            setHoveredIndex(null);
                                        },
                                    },
                                },
                                labels,
                                fill: {
                                    colors,
                                },

                                legend: {
                                    show: false,
                                },
                                tooltip: {
                                    enabled: false,
                                },
                            }}
                            type='donut'
                            width={'400px'}
                        />
                    ) : (
                        <Typography>{t('noDataAvailable')}</Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        mt: 4,
                        mr: 'auto',
                    }}
                >
                    <Stack spacing={2}>
                        {labels.map((label, index) => {
                            const isHovered = hoveredIndex === index;
                            const isOtherHovered =
                                hoveredIndex !== null && hoveredIndex !== index;

                            return (
                                <Stack
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        opacity: isOtherHovered ? 0.4 : 1,
                                        fontWeight: isHovered
                                            ? 'bold'
                                            : 'normal',
                                        transition: 'opacity 0.2s ease',
                                    }}
                                    spacing={2}
                                    direction={'row'}
                                    key={index}
                                >
                                    <LegenDot color={colors[index]} />
                                    <Typography
                                        fontWeight={
                                            isHovered ? 'bold' : 'normal'
                                        }
                                    >
                                        {label}
                                        {counts?.[index] !== undefined && (
                                            <Typography
                                                component='span'
                                                sx={{
                                                    ml: 1,
                                                    color: 'text.secondary',
                                                }}
                                                fontWeight={
                                                    isHovered
                                                        ? 'bold'
                                                        : 'normal'
                                                }
                                            >
                                                ({counts[index]})
                                            </Typography>
                                        )}
                                    </Typography>
                                </Stack>
                            );
                        })}
                        {counts && counts.length > 0 && (
                            <Stack
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 1,
                                    pt: 1,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                }}
                                spacing={2}
                                direction={'row'}
                            >
                                <Typography fontWeight='bold'>
                                    {t('total')}
                                    <Typography
                                        component='span'
                                        sx={{
                                            ml: 1,
                                            color: 'text.secondary',
                                        }}
                                        fontWeight='bold'
                                    >
                                        ({totalCount})
                                    </Typography>
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );
};

const LegenDot = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ theme, color }) => ({
    backgroundColor: color,
    borderRadius: theme.spacing(1),
    width: theme.spacing(3),
    height: theme.spacing(3),
}));
